import { JSX } from 'react';
import {
    Datagrid,
    DateField,
    DateInput,
    FilterButton,
    List,
    SelectField,
    SelectInput,
    TextField,
    TextInput,
    TopToolbar,
} from 'react-admin';
import { emailMessageDirectionOptions } from '../../defaults/defaults';
import { emailMessageChannels, emailMessageStatusChoices } from '../show/email-message-format';

/**
 * Every email HQ-API has stored, both directions and both channels, read only.
 *
 * Which rows an admin sees is HQ-API's decision, not this screen's: `EmailMessageChannelExtension`
 * narrows the collection to the channels the caller holds (Nok pipeline mail needs
 * `HQ_Nok::noks`, sales correspondence needs `HQ_Sales::conversation`). The channel filter below
 * only narrows further within that.
 *
 * Each filter maps onto an `ApiFilter` on `EmailMessage`; one without a filter there would be
 * accepted by HQ-API and silently ignored.
 */
const filters: JSX.Element[] = [
    <TextInput key="subject" source="subject" label="Subject" alwaysOn />,
    <TextInput key="sender" source="sender" label="From" alwaysOn />,
    <TextInput key="recipient" source="recipient" label="To" />,
    <SelectInput key="channel" source="channel" choices={ emailMessageChannels } alwaysOn />,
    <SelectInput key="direction" source="direction" choices={ emailMessageDirectionOptions } alwaysOn />,
    <SelectInput key="status" source="status" choices={ emailMessageStatusChoices } />,
    <DateInput key="after" source="createdAt[after]" label="From date" />,
    <DateInput key="before" source="createdAt[before]" label="To date" />,
];

/** The filter button only: nothing here creates a message, and an export would carry every body. */
const ListActions: () => JSX.Element = (): JSX.Element => (
    <TopToolbar>
        <FilterButton />
    </TopToolbar>
);

export const EmailMessagesList: () => JSX.Element = (): JSX.Element => (
    <List
        filters={ filters }
        sort={ { field: 'createdAt', order: 'DESC' } }
        actions={ <ListActions /> }
        title={ false }
    >
        <Datagrid bulkActionButtons={ false } rowClick="show">
            <TextField source="id" />
            <DateField source="createdAt" label="Date" showTime />
            <SelectField source="channel" choices={ emailMessageChannels } sortable={ false } />
            <SelectField source="direction" choices={ emailMessageDirectionOptions } sortable={ false } />
            <TextField source="sender" label="From" sortable={ false } />
            <TextField source="recipient" label="To" sortable={ false } />
            <TextField source="subject" />
            <SelectField source="status" choices={ emailMessageStatusChoices } sortable={ false } />
        </Datagrid>
    </List>
);
